import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Spin } from "antd";
import { useSelector } from "react-redux";

import * as projectsApi from "../../../../core/data/redux/services/projectsApi";
import {
  selectActiveCompany,
  selectActiveSprint,
  selectCurrentProject,
  selectIssuesByProject,
  selectStatuses,
} from "../../../../core/data/redux/projects/selectors";
import { STATUS_CATEGORY_COLORS } from "../../constants/projects.constants";

import type { ProjectStats } from "../../../../core/data/redux/projects/types";

interface StatsResponse {
  stats: ProjectStats;
  by_status: Record<string, number>;
  by_assignee: Record<string, number>;
  by_sprint: Record<string, number>;
}

const cardStyle: React.CSSProperties = {
  borderRadius: 14,
  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
  border: "none",
};

const ProjectReports: React.FC = () => {
  const project = useSelector(selectCurrentProject);
  const activeCompany = useSelector(selectActiveCompany);
  const statuses = useSelector(selectStatuses);
  const activeSprint = useSelector(selectActiveSprint);
  const projectIssues = useSelector(
    selectIssuesByProject(project?.id ?? null)
  );

  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!project?.id || !activeCompany.company_id) return;
    let cancelled = false;
    setLoading(true);
    projectsApi
      .getProjectStats(
        activeCompany.company_id,
        activeCompany.company_type,
        project.id
      )
      .then((data: any) => {
        if (cancelled) return;
        setStats(data || null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [project?.id, activeCompany.company_id, activeCompany.company_type]);

  const statusBreakdown = useMemo(() => {
    if (!stats) return [];
    return statuses
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((s) => ({
        name: s.name,
        category: s.category,
        color: s.color || STATUS_CATEGORY_COLORS[s.category],
        count: Number(stats.by_status?.[String(s.id)] || 0),
      }));
  }, [stats, statuses]);

  const burndown = useMemo(() => {
    if (!activeSprint || !projectIssues) return null;
    const sprintIssues = projectIssues.filter(
      (i) => i.is_active && i.sprint_id === activeSprint.id
    );
    if (sprintIssues.length === 0) return null;

    const startDate = activeSprint.started_at || activeSprint.start_date;
    const endDate = activeSprint.end_date;
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
      return null;

    const today = new Date();
    const cutoff = today < end ? today : end;

    const totalPoints = sprintIssues.reduce(
      (sum, i) => sum + (i.story_points || 1),
      0
    );

    const days: Date[] = [];
    const cur = new Date(start);
    while (cur <= end) {
      days.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    if (days.length === 0) return null;

    const idealStep = totalPoints / Math.max(days.length - 1, 1);
    const ideal = days.map((_, idx) =>
      +(totalPoints - idealStep * idx).toFixed(2)
    );

    const doneStatusIds = new Set(
      statuses.filter((s) => s.category === "DONE").map((s) => s.id)
    );

    const remaining = days.map((day) => {
      if (day > cutoff) return null;
      let remainingPoints = 0;
      for (const issue of sprintIssues) {
        const isDone = doneStatusIds.has(issue.status_id);
        const updatedAt = new Date(issue.updated_at);
        const wasDoneByDay = isDone && updatedAt <= day;
        if (!wasDoneByDay) {
          remainingPoints += issue.story_points || 1;
        }
      }
      return remainingPoints;
    });

    return {
      categories: days.map((d) => d.toISOString().slice(5, 10)),
      ideal,
      remaining,
      totalPoints,
    };
  }, [activeSprint, projectIssues, statuses]);

  const throughput = useMemo(() => {
    if (!projectIssues) return null;
    const doneStatusIds = new Set(
      statuses.filter((s) => s.category === "DONE").map((s) => s.id)
    );
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 7 * 12);
    start.setHours(0, 0, 0, 0);

    const labels: string[] = [];
    const counts: number[] = [];
    for (let i = 0; i < 12; i++) {
      const wkStart = new Date(start);
      wkStart.setDate(wkStart.getDate() + i * 7);
      const wkEnd = new Date(wkStart);
      wkEnd.setDate(wkEnd.getDate() + 7);
      const label = wkStart.toISOString().slice(5, 10);
      const completed = projectIssues.filter((iss) => {
        if (!iss.is_active) return false;
        if (!doneStatusIds.has(iss.status_id)) return false;
        const upd = new Date(iss.updated_at);
        return upd >= wkStart && upd < wkEnd;
      }).length;
      labels.push(label);
      counts.push(completed);
    }
    return { labels, counts };
  }, [projectIssues, statuses]);

  if (loading || !project) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-white"
        style={{ minHeight: 300, borderRadius: 14 }}
      >
        <div className="text-center">
          <Spin size="large" />
          <p className="text-muted mt-3 mb-0" style={{ fontSize: 13 }}>
            Loading reports...
          </p>
        </div>
      </div>
    );
  }

  const totalIssues = stats?.stats?.total ?? 0;
  const done = stats?.stats?.by_status_category?.DONE ?? 0;
  const inProgress = stats?.stats?.by_status_category?.IN_PROGRESS ?? 0;
  const todo = stats?.stats?.by_status_category?.TODO ?? 0;
  const completionPct =
    totalIssues > 0 ? Math.round((done / totalIssues) * 100) : 0;

  return (
    <div className="d-flex flex-column gap-3">
      {/* Summary cards */}
      <div className="row g-3">
        <div className="col-md-3">
          <SummaryCard
            icon="ti ti-list-check"
            label="Total Issues"
            value={totalIssues}
            iconColor="#3b82f6"
            iconBg="#eff6ff"
          />
        </div>
        <div className="col-md-3">
          <SummaryCard
            icon="ti ti-clock"
            label="To Do"
            value={todo}
            color={STATUS_CATEGORY_COLORS.TODO}
            iconColor={STATUS_CATEGORY_COLORS.TODO}
            iconBg="#f1f5f9"
          />
        </div>
        <div className="col-md-3">
          <SummaryCard
            icon="ti ti-progress"
            label="In Progress"
            value={inProgress}
            color={STATUS_CATEGORY_COLORS.IN_PROGRESS}
            iconColor={STATUS_CATEGORY_COLORS.IN_PROGRESS}
            iconBg="#eff6ff"
          />
        </div>
        <div className="col-md-3">
          <SummaryCard
            icon="ti ti-circle-check"
            label="Done"
            value={`${done} (${completionPct}%)`}
            color={STATUS_CATEGORY_COLORS.DONE}
            iconColor={STATUS_CATEGORY_COLORS.DONE}
            iconBg="#ecfdf5"
          />
        </div>
      </div>

      <div className="row g-3">
        {/* Burndown */}
        <div className="col-lg-7">
          <div className="card h-100" style={cardStyle}>
            <div className="card-header border-0 pb-0">
              <h6 className="mb-0 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
                <i className="ti ti-chart-line" style={{ fontSize: 18, color: "#3b82f6" }} />
                {activeSprint
                  ? `Sprint Burndown: ${activeSprint.name}`
                  : "Burndown"}
              </h6>
            </div>
            <div className="card-body bg-white rounded-3">
              {!activeSprint ? (
                <div className="text-center py-4">
                  <i className="ti ti-chart-line" style={{ fontSize: 36, color: "#cbd5e1" }} />
                  <p className="text-muted mb-0 mt-2" style={{ fontSize: 13 }}>
                    Burndown is only shown when a sprint is active. Start a
                    sprint from the Backlog page to see it here.
                  </p>
                </div>
              ) : !burndown ? (
                <div className="text-center py-4">
                  <i className="ti ti-chart-off" style={{ fontSize: 36, color: "#cbd5e1" }} />
                  <p className="text-muted mb-0 mt-2" style={{ fontSize: 13 }}>
                    Sprint has no issues with story points or no start/end dates yet.
                  </p>
                </div>
              ) : (
                <ReactApexChart
                  type="line"
                  height={320}
                  series={[
                    {
                      name: "Ideal",
                      data: burndown.ideal,
                    },
                    {
                      name: "Remaining",
                      data: burndown.remaining as any,
                    },
                  ]}
                  options={{
                    chart: { toolbar: { show: false }, fontFamily: "inherit" },
                    stroke: { curve: "smooth", width: [2, 3] },
                    colors: ["#9CA3AF", "#3B82F6"],
                    xaxis: { categories: burndown.categories },
                    yaxis: {
                      title: { text: "Story points", style: { fontSize: "12px", color: "#94a3b8" } },
                      min: 0,
                    },
                    legend: { position: "top" },
                    dataLabels: { enabled: false },
                    grid: { borderColor: "#f1f5f9" },
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Throughput */}
        <div className="col-lg-5">
          <div className="card h-100" style={cardStyle}>
            <div className="card-header border-0 pb-0">
              <h6 className="mb-0 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
                <i className="ti ti-chart-bar" style={{ fontSize: 18, color: "#10b981" }} />
                Throughput (weekly)
              </h6>
            </div>
            <div className="card-body bg-white rounded-3">
              {!throughput || throughput.counts.every((c) => c === 0) ? (
                <div className="text-center py-4">
                  <i className="ti ti-chart-bar" style={{ fontSize: 36, color: "#cbd5e1" }} />
                  <p className="text-muted mb-0 mt-2" style={{ fontSize: 13 }}>
                    No completed issues in the last 12 weeks yet.
                  </p>
                </div>
              ) : (
                <ReactApexChart
                  type="bar"
                  height={320}
                  series={[
                    {
                      name: "Completed",
                      data: throughput.counts,
                    },
                  ]}
                  options={{
                    chart: { toolbar: { show: false }, fontFamily: "inherit" },
                    colors: [STATUS_CATEGORY_COLORS.DONE],
                    xaxis: { categories: throughput.labels },
                    plotOptions: {
                      bar: { borderRadius: 6, columnWidth: "55%" },
                    },
                    dataLabels: { enabled: false },
                    grid: { borderColor: "#f1f5f9" },
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Issues by Column */}
        <div className="col-12">
          <div className="card" style={cardStyle}>
            <div className="card-header border-0 pb-0">
              <h6 className="mb-0 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
                <i className="ti ti-columns" style={{ fontSize: 18, color: "#f59e0b" }} />
                Issues by Column
              </h6>
            </div>
            <div className="card-body bg-white rounded-3">
              {statusBreakdown.length === 0 ? (
                <div className="text-center py-4">
                  <i className="ti ti-columns" style={{ fontSize: 36, color: "#cbd5e1" }} />
                  <p className="text-muted mb-0 mt-2" style={{ fontSize: 13 }}>
                    No statuses configured.
                  </p>
                </div>
              ) : (
                <ReactApexChart
                  type="bar"
                  height={280}
                  series={[
                    {
                      name: "Issues",
                      data: statusBreakdown.map((s) => s.count),
                    },
                  ]}
                  options={{
                    chart: { toolbar: { show: false }, fontFamily: "inherit" },
                    plotOptions: {
                      bar: {
                        horizontal: true,
                        borderRadius: 6,
                        distributed: true,
                      },
                    },
                    colors: statusBreakdown.map((s) => s.color),
                    xaxis: {
                      categories: statusBreakdown.map((s) => s.name),
                    },
                    legend: { show: false },
                    dataLabels: { enabled: true },
                    grid: { borderColor: "#f1f5f9" },
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface SummaryCardProps {
  icon: string;
  label: string;
  value: number | string;
  color?: string;
  iconColor: string;
  iconBg: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ icon, label, value, color, iconColor, iconBg }) => (
  <div
    className="card h-100"
    style={{
      ...cardStyle,
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      cursor: "default",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 1px 8px rgba(0,0,0,0.06)";
    }}
  >
    <div className="card-body bg-white rounded-3 d-flex align-items-center gap-3" style={{ padding: "14px 16px" }}>
      <div
        className="d-flex align-items-center justify-content-center rounded"
        style={{
          width: 40,
          height: 40,
          background: iconBg,
          borderRadius: 10,
          flexShrink: 0,
        }}
      >
        <i className={icon} style={{ fontSize: 20, color: iconColor }} />
      </div>
      <div>
        <div className="fw-bold" style={{ fontSize: 18, color: color || "#0f172a" }}>
          {value}
        </div>
        <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
      </div>
    </div>
  </div>
);

export default ProjectReports;
